import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { ServiceConsentRoutingEnum } from '@features/services/service-consents-page-wrapper/service-consents-routing.enum';
import { ServicesRoutingEnum } from '@features/services/services-routing.enum';
import { Store } from '@ngrx/store';
import { servicesAuthorizations } from '@stores/profile/profile.selectors';
import { isServiceUnavailableInUserPlan } from '@stores/selectors/profile-services.selectors';
import { activateService } from '@stores/services/services.actions';
import { SERVICE_REDIRECT_URL_QUERY_PARAM_KEY, areConsentsRequired } from '@stores/services/services.utils';
import { selectIfDefined } from '@stores/utils/selects.operators';
import { combineLatest, map, take } from 'rxjs';

type CanAccessServiceFeatureOptions = { redirectWhenConsentsAccepted?: boolean; mustActivate?: boolean };

export const canAccessServiceFeature: (options?: CanAccessServiceFeatureOptions) => CanActivateFn =
  (options?: CanAccessServiceFeatureOptions) => (_: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const store = inject(Store);
    const router = inject(Router);

    const serviceRegExpMatch = new RegExp(`${FeaturesRoutingEnum.Services}\/([^\/]*)\/`).exec(state.url);
    const serviceId = serviceRegExpMatch?.[1];

    if (!serviceId) return false;

    return combineLatest([selectIfDefined(store, servicesAuthorizations), areConsentsRequired(serviceId, store)]).pipe(
      take(1),
      map(([servicesAuthorizations, areConsentsRequired]) => {
        // List of services for which we do not check for consents if user is freemium
        const hasNotAccessServiceCheckList = [ServicesRoutingEnum.Drive, ServicesRoutingEnum.Home];

        let hasAccess = true;
        for (let i = 0; i <= 1; i++) {
          if (
            hasNotAccessServiceCheckList[i] === serviceId &&
            store.selectSignal(isServiceUnavailableInUserPlan(hasNotAccessServiceCheckList[i]))()
          ) {
            hasAccess = false;
            break;
          }
        }

        const serviceIsAuthorized = servicesAuthorizations?.find(
          (authorization) => authorization.applicationId === serviceId
        );

        if (options?.mustActivate && hasAccess && !serviceIsAuthorized && !areConsentsRequired) {
          const targetRoutePath = ['/', ...state.url.split('/').filter((v) => !!v)];
          store.dispatch(activateService({ serviceId, mustRedirect: true, targetRoutePath }));
        }

        if (!hasAccess || serviceIsAuthorized || !areConsentsRequired) return true;

        const consentUrl = router.parseUrl(
          `/${FeaturesRoutingEnum.Services}/${serviceId}/${ServiceConsentRoutingEnum.Consent}`
        );

        if (options?.redirectWhenConsentsAccepted) {
          consentUrl.queryParams[SERVICE_REDIRECT_URL_QUERY_PARAM_KEY] = state.url;
        }

        return consentUrl;
      })
    );
  };
